import { defineStore } from 'pinia';
import api from '../api/axios';
import { resetAdminData } from '../api/adminData';
import { resetContentData } from '../api/contentData';
import { managementScope } from '../auth/management-context.mjs';
import {
    buildBrowserSuccessUrl, clearBrowserLoginState, loadBrowserLoginState,
    pollBrowserSession, saveBrowserLoginState, safeReturnPath,
} from '../auth/browser-session.mjs';

let profileRequest = null;
let completionRequest = null;
let loginRequest = null;

export const useAdminAuthStore = defineStore('adminAuth', {
    state: () => ({
        user: null, isAuthenticated: false, profileVerified: false,
        identityGeneration: 0, requestGeneration: 0,
        logoutError: '',
    }),
    actions: {
        setAuth(user) {
            if (this.user?.email !== user?.email) {
                resetAdminData();
                resetContentData();
                this.identityGeneration += 1;
            }
            this.user = user;
            this.isAuthenticated = !!user;
            this.profileVerified = !!user;
            localStorage.removeItem('admin_user');
        },
        clearAuth(invalidateRequests = true) {
            managementScope.invalidate();
            if (invalidateRequests) this.requestGeneration += 1;
            this.setAuth(null);
        },
        beginBrowserLogin(returnPath = '/users') {
            if (loginRequest) return loginRequest;
            const generation = this.requestGeneration;
            const operation = (async () => {
                try {
                    const user = await this.fetchProfile();
                    if (generation !== this.requestGeneration) return;
                    if (user) {
                        clearBrowserLoginState(window.sessionStorage);
                        window.location.replace(safeReturnPath(returnPath));
                        return;
                    }
                } catch (error) {
                    if (![401, 403].includes(error.response?.status)) throw error;
                }
                if (generation !== this.requestGeneration) return;
                const response = await api.post('/auth/browser-session/', {
                    success_url: buildBrowserSuccessUrl(window.location),
                });
                if (generation !== this.requestGeneration) return;
                saveBrowserLoginState(window.sessionStorage, response.data, returnPath);
                window.location.replace(response.data.login_url);
            })();
            loginRequest = operation.finally(() => { loginRequest = null; });
            return loginRequest;
        },
        completeBrowserLogin() {
            if (completionRequest) return completionRequest;
            const generation = this.requestGeneration;
            const operation = (async () => {
                const session = loadBrowserLoginState(window.sessionStorage);
                try {
                    const user = await this.fetchProfile();
                    if (generation !== this.requestGeneration) throw new Error('登录已取消。');
                    if (user) {
                        clearBrowserLoginState(window.sessionStorage);
                        return session?.return_path || '/users';
                    }
                } catch (error) {
                    if (![401, 403].includes(error.response?.status)) throw error;
                }
                if (generation !== this.requestGeneration) throw new Error('登录已取消。');
                if (!session) {
                    const error = new Error('未找到待完成的登录会话，请重新登录。');
                    error.sessionExpired = true;
                    throw error;
                }
                try {
                    const result = await pollBrowserSession({
                        api, session, isCurrent: () => generation === this.requestGeneration,
                    });
                    if (generation !== this.requestGeneration) throw new Error('登录已取消。');
                    this.requestGeneration += 1;
                    this.setAuth(result.user);
                    clearBrowserLoginState(window.sessionStorage);
                    return session.return_path;
                } catch (error) {
                    if (error.sessionExpired) clearBrowserLoginState(window.sessionStorage);
                    throw error;
                }
            })();
            completionRequest = operation.finally(() => { completionRequest = null; });
            return completionRequest;
        },
        fetchProfile() {
            if (profileRequest && profileRequest.generation === this.requestGeneration) return profileRequest.promise;
            const generation = this.requestGeneration;
            const operation = (async () => {
                try {
                    const response = await api.get('/auth/me/');
                    if (generation !== this.requestGeneration) return null;
                    this.setAuth(response.data);
                    return this.user;
                } catch (error) {
                    if (generation === this.requestGeneration && [401, 403].includes(error.response?.status)) {
                        this.clearAuth(false);
                        if (error.response.status === 403) error.permissionDenied = true;
                    }
                    throw error;
                }
            })();
            const pending = { generation, promise: null };
            profileRequest = pending;
            pending.promise = operation.finally(() => { if (profileRequest === pending) profileRequest = null; });
            return pending.promise;
        },
        async logout() {
            managementScope.invalidate();
            this.requestGeneration += 1;
            const generation = this.requestGeneration;
            this.logoutError = '';
            clearBrowserLoginState(window.sessionStorage);
            try {
                const response = await api.post('/auth/logout/');
                if (generation !== this.requestGeneration) return;
                this.clearAuth(false);
                window.location.href = `${managementScope.prefix}/management-ended?${response.status === 202 || response.data.status === 'pending' ? 'pending=1' : 'logged_out=1'}`;
            } catch (error) {
                if (generation !== this.requestGeneration) return;
                this.logoutError = '退出未完成，请重试。';
                throw error;
            }
        },
    },
});
